import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Modal, SearchField, Tile } from '../../../ui-kit';
import { ReactComponent as HubspotLogo } from '../../../assets/connectors/hubspot.svg';
import { ReactComponent as RedshiftLogo } from '../../../assets/connectors/redshift.svg';
import { ReactComponent as PostgresLogo } from '../../../assets/connectors/postgres.svg';
import { ReactComponent as SnowflakeLogo } from '../../../assets/connectors/Snowflake.svg';
import { ReactComponent as BigQueryLogo } from '../../../assets/connectors/bigquery.svg';
import { ReactComponent as GoogleAdsLogo } from '../../../assets/connectors/google-ads.svg';
import { ReactComponent as GoogleDriveLogo } from '../../../assets/connectors/google-drive.svg';
import { ReactComponent as AirtableLogo } from '../../../assets/connectors/airtable.svg';
import { ReactComponent as SalesforceLogo } from '../../../assets/connectors/salesforce.svg';
import { ReactComponent as ZohoLogo } from '../../../assets/connectors/zoho.svg';
import { ReactComponent as AircallLogo } from '../../../assets/connectors/aircall.svg';
import { ReactComponent as TwilioLogo } from '../../../assets/connectors/twilio.svg';
import { ReactComponent as ElevenLabsLogo } from '../../../assets/connectors/elevenlabs.svg';
import regalLogo from '../../../assets/connectors/regal.png';
import genesysLogo from '../../../assets/connectors/genesys.png';
import dialpadLogo from '../../../assets/connectors/dialpad.png';
import pipedriveLogo from '../../../assets/connectors/pipedrive.png';
import WebhookRounded from '@mui/icons-material/WebhookRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import SmsRounded from '@mui/icons-material/SmsRounded';
import MailRounded from '@mui/icons-material/MailRounded';
import type { SvgIconComponent } from '@mui/icons-material';

interface Choice {
  id: string;
  name: string;
  /** What joining it would give this agent. */
  purpose: string;
  /** Where it plugs in, for the window that opens on an adaptor. */
  plugsIn: string;
  group: 'Leads in' | 'Calls out' | 'Messaging' | 'Data';
  logo?: React.ReactNode;
}

/** A company's own mark, from a PNG in the assets folder. */
function Mark({ src, name, size = 24 }: { src: string; name: string; size?: number }) {
  return (
    <Box
      component="img"
      src={src}
      alt={`${name} logo`}
      sx={(t) => ({ width: size, height: size, borderRadius: `${t.ds.radius.xs}px`, display: 'block' })}
    />
  );
}

/** The Klaris mark, for what Klaris itself provides. */
export function KlarisMark({ size = 24 }: { size?: number }) {
  return <Mark src="/klaris-logo.svg" name="Klaris" size={size} />;
}

/** A generic channel with no company behind it: the app's own icon. */
function Glyph({ icon: Icon }: { icon: SvgIconComponent }) {
  return <Icon sx={(t) => ({ fontSize: 24, color: t.tokens.text.secondary })} />;
}

/** The two systems the Klaris AI Dialer is built on, small, for a subtitle. */
export const DIALER_PARTS: Record<string, React.ReactNode> = {
  ElevenLabs: <ElevenLabsLogo width={14} height={14} />,
  Twilio: <TwilioLogo width={14} height={14} />,
};

const CHOICES: Choice[] = [
  { id: 'hubspot', name: 'HubSpot', purpose: 'Leads and contacts from the CRM', plugsIn: 'Beside or in place of HubSpot: new records land in the warehouse, and ROCCO reads them from there.', group: 'Leads in', logo: <HubspotLogo width={24} height={24} /> },
  { id: 'salesforce', name: 'Salesforce', purpose: 'Leads and contacts from the CRM', plugsIn: 'Beside or in place of HubSpot: new records land in the warehouse, and ROCCO reads them from there.', group: 'Leads in', logo: <SalesforceLogo width={24} height={24} /> },
  { id: 'zoho', name: 'Zoho CRM', purpose: 'Leads and contacts from the CRM', plugsIn: 'Beside or in place of HubSpot: new records land in the warehouse, and ROCCO reads them from there.', group: 'Leads in', logo: <ZohoLogo width={24} height={24} /> },
  { id: 'pipedrive', name: 'Pipedrive', purpose: 'Leads and deals from the CRM', plugsIn: 'Beside or in place of HubSpot: new records land in the warehouse, and ROCCO reads them from there.', group: 'Leads in', logo: <Mark src={pipedriveLogo} name="Pipedrive" /> },
  { id: 'webhook', name: 'Webhook', purpose: 'Leads posted in as they are captured', plugsIn: 'A web form or ad platform posts each lead as it is captured; it lands in the warehouse like any other.', group: 'Leads in', logo: <Glyph icon={WebhookRounded} /> },
  { id: 'csv', name: 'File drop', purpose: 'A list dropped in, for bought data', plugsIn: 'A CSV dropped in a folder is loaded into the warehouse; each row becomes a lead.', group: 'Leads in', logo: <Glyph icon={UploadFileRounded} /> },

  { id: 'dialer', name: 'Klaris AI Dialer', purpose: 'AI voice agents on ElevenLabs, calling over Twilio', plugsIn: 'Built in: Ava, Miles and Nora place the calls, and the outcome comes straight back.', group: 'Calls out', logo: <KlarisMark /> },
  { id: 'regal', name: 'Regal', purpose: 'Places the calls and records them', plugsIn: 'In place of the Klaris AI Dialer: ROCCO hands it each call, it dials and records, and the outcome comes back the same way.', group: 'Calls out', logo: <Mark src={regalLogo} name="Regal" /> },
  { id: 'genesys', name: 'Genesys', purpose: 'Contact centre routing and dialling', plugsIn: 'In place of the Klaris AI Dialer: ROCCO hands it each call, it dials and records, and the outcome comes back the same way.', group: 'Calls out', logo: <Mark src={genesysLogo} name="Genesys" /> },
  { id: 'dialpad', name: 'Dialpad', purpose: 'Places the calls and records them', plugsIn: 'In place of the Klaris AI Dialer: ROCCO hands it each call, it dials and records, and the outcome comes back the same way.', group: 'Calls out', logo: <Mark src={dialpadLogo} name="Dialpad" /> },
  { id: 'aircall', name: 'Aircall', purpose: 'Places the calls and records them', plugsIn: 'In place of the Klaris AI Dialer: ROCCO hands it each call, it dials and records, and the outcome comes back the same way.', group: 'Calls out', logo: <AircallLogo width={24} height={24} /> },

  { id: 'sms', name: 'SMS', purpose: 'Follow up by text between calls', plugsIn: 'Between calls: a text after a missed call, and a reminder before the callback.', group: 'Messaging', logo: <Glyph icon={SmsRounded} /> },
  { id: 'email', name: 'Email', purpose: 'Confirm the callback in writing', plugsIn: 'After a call: the agreed time, and who will ring.', group: 'Messaging', logo: <Glyph icon={MailRounded} /> },

  { id: 'warehouse', name: 'Redshift', purpose: 'Read leads and history from the warehouse', plugsIn: 'As the warehouse ROCCO reads leads from and writes to, in place of the current one.', group: 'Data', logo: <RedshiftLogo width={24} height={24} /> },
  { id: 'snowflake', name: 'Snowflake', purpose: 'Read leads and history from the warehouse', plugsIn: 'As the warehouse ROCCO reads leads from and writes to, in place of the current one.', group: 'Data', logo: <SnowflakeLogo width={24} height={24} /> },
  { id: 'bigquery', name: 'BigQuery', purpose: 'Read leads and history from the warehouse', plugsIn: 'As the warehouse ROCCO reads leads from and writes to, in place of the current one.', group: 'Data', logo: <BigQueryLogo width={24} height={24} /> },
  { id: 'postgres', name: 'Postgres', purpose: 'Read leads and history from a database', plugsIn: 'As the warehouse ROCCO reads leads from and writes to, in place of the current one.', group: 'Data', logo: <PostgresLogo width={24} height={24} /> },
  { id: 'google-ads', name: 'Google Ads', purpose: 'What each lead cost to buy', plugsIn: 'Beside the warehouse: what each lead cost, for ordering the queue by value.', group: 'Data', logo: <GoogleAdsLogo width={24} height={24} /> },
  { id: 'drive', name: 'Google Drive', purpose: 'Scripts and rate cards', plugsIn: 'What the agents may say: scripts and rate cards, read before each call.', group: 'Data', logo: <GoogleDriveLogo width={24} height={24} /> },
  { id: 'airtable', name: 'Airtable', purpose: 'Lists kept outside the CRM', plugsIn: 'Lists kept outside the CRM, loaded into the warehouse.', group: 'Data', logo: <AirtableLogo width={24} height={24} /> },
]

const GROUPS: Choice['group'][] = ['Leads in', 'Calls out', 'Messaging', 'Data'];

/**
 * Everything this agent could be joined to, to pick from.
 *
 * Searchable because the list is long enough that scanning it is slower than
 * typing, and grouped by what the connection is *for* rather than by vendor —
 * somebody adding a dialer does not want to read past seven warehouses.
 */
export default function IntegrationPicker({
  open, onClose, onPick, connectedIds,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (id: string) => void;
  /** Which of these are joined up now, from the live integrations. */
  connectedIds: string[];
}) {
  const [query, setQuery] = React.useState('');

  const term = query.trim().toLowerCase();
  const matches = CHOICES.filter((choice) => !term
    || choice.name.toLowerCase().includes(term)
    || choice.purpose.toLowerCase().includes(term)
    || choice.group.toLowerCase().includes(term));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New integration"
      subtitle="What this agent reads from, and what it calls through"
      size="lg"
    >
      <Stack spacing={2}>
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Search systems…"
          autoFocus
        />

        {matches.length === 0 ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary, py: 2 })}>
            Nothing matches “{query}”. Every system can also be reached over a webhook.
          </Typography>
        ) : null}

        {GROUPS.map((group) => {
          const rows = matches.filter((choice) => choice.group === group);
          if (!rows.length) return null;
          return (
            <Stack key={group} spacing={1}>
              <Typography variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                {group}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                }}
              >
                {rows.map((choice) => (
                  <Tile
                    key={choice.id}
                    title={choice.name}
                    description={choice.purpose}
                    meta={connectedIds.includes(choice.id) ? 'Connected' : undefined}
                    icon={choice.logo}
                    selected={connectedIds.includes(choice.id)}
                    onClick={() => { onPick(choice.id); onClose(); }}
                  />
                ))}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Modal>
  );
}

export { CHOICES as INTEGRATION_CHOICES };
export type { Choice as IntegrationChoice };
